import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { useUpdateBrandSettingsAction } from '@/hooks/actions/use-update-brand-settings-action';
import { Box, Button, createListCollection, Flex } from '@chakra-ui/react';
import type { Brand } from '@fluenceur/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from './SettingsSection';

interface LanguagePreferenceSectionProps {
  brand: Brand;
  isLast?: boolean;
}

const LanguagePreferenceSection = ({ brand, isLast }: LanguagePreferenceSectionProps) => {
  const { t } = useTranslation();
  const brandLanguage = brand.settings?.preferredLanguage || 'en';
  const [selectedLanguage, setSelectedLanguage] = useState<string>(brandLanguage);
  const [originalLanguage, setOriginalLanguage] = useState<string>(brandLanguage);
  const updateSettings = useUpdateBrandSettingsAction(brand.id);

  useEffect(() => {
    const nextLanguage = brand.settings?.preferredLanguage || 'en';
    setSelectedLanguage(nextLanguage);
    setOriginalLanguage(nextLanguage);
  }, [brand.settings?.preferredLanguage]);

  const savedLanguage =
    updateSettings.data?.data?.settings?.preferredLanguage ??
    updateSettings.variables?.preferredLanguage;

  useEffect(() => {
    if (!updateSettings.isSuccess || !savedLanguage) {
      return;
    }
    setOriginalLanguage(savedLanguage);
    setSelectedLanguage(savedLanguage);
  }, [savedLanguage, updateSettings.isSuccess]);

  const languageCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { label: t('settings.brandLanguage.english'), value: 'en' },
          { label: t('settings.brandLanguage.french'), value: 'fr' },
          { label: t('settings.brandLanguage.italian'), value: 'it' },
        ],
      }),
    [t]
  );

  const handleSave = () => {
    const language = selectedLanguage as 'en' | 'fr' | 'it';
    updateSettings.mutate({ preferredLanguage: language });
  };

  const hasChanges = selectedLanguage !== originalLanguage;

  return (
    <SettingsSection
      title={t('settings.brandLanguage.title')}
      description={t('settings.brandLanguage.description')}
      isLast={isLast}
    >
      <Box maxW="400px" w="full">
        <SelectRoot
          collection={languageCollection}
          value={[selectedLanguage]}
          onValueChange={(e) => setSelectedLanguage(e.value[0] ?? '')}
          size="md"
        >
          <SelectTrigger>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent>
            {languageCollection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Box>

      <Flex gap={2} mt={4}>
        <Button
          size="sm"
          disabled={!hasChanges || updateSettings.isPending}
          onClick={handleSave}
          loading={updateSettings.isPending}
        >
          {t('settings.brandLanguage.save')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setSelectedLanguage(originalLanguage)}
          disabled={!hasChanges || updateSettings.isPending}
        >
          {t('common.cancel')}
        </Button>
      </Flex>
    </SettingsSection>
  );
};

export default LanguagePreferenceSection;
